// Chart palette + chrome for the report pages.
//
// The eight categorical slots below are NOT arbitrary — the slot *ordering* is the
// colour-blind-safety mechanism, and the set was validated against the white MUI
// Paper surface with the dataviz validator:
//
//   [PASS] Lightness band      all 7 inside L 0.43-0.77
//   [PASS] Chroma floor        all 7 >= 0.1
//   [PASS] CVD separation      worst adjacent #eda100<->#1baf7a dE 9.1 (protan)
//   [PASS] Normal-vision floor worst adjacent #e87ba4<->#eda100 dE 19.6
//   [WARN] Contrast vs surface #1baf7a 2.82, #eda100 2.17, #e87ba4 2.69
//
// The contrast WARN obliges "relief": three slots sit below 3:1 on white, so a
// chart using them MUST also offer visible labels or a table view. ScanTrendChart
// ships the table toggle for exactly this reason — do not remove it.
//
// If you change a hex, re-run the validator; do not eyeball a replacement:
//   node scripts/validate_palette.js "<comma,separated,hexes>" \
//        --mode light --surface "#ffffff"

// Categorical slots, in fixed order. Never cycle this array to seat a 9th series —
// a generated hue is indistinguishable from an existing one under CVD. Past
// MAX_SERIES, fold the tail into OTHER instead.
export const SERIES_COLORS = [
    '#2a78d6', // 1 blue
    '#eb6834', // 2 orange
    '#1baf7a', // 3 aqua
    '#eda100', // 4 yellow
    '#e87ba4', // 5 magenta
    '#008300', // 6 green
    '#4a3aa7', // 7 violet
];

// The folded tail. Deliberately a neutral grey so it recedes behind the real series.
export const OTHER_COLOR = '#898781';
export const OTHER_KEY = '__other__';
export const OTHER_LABEL = 'Other';

// How many real series get their own hue before the rest fold into "Other".
export const MAX_SERIES = SERIES_COLORS.length;

// Chart chrome. Grid and axis ink stay recessive so the data is the loudest thing
// on the page.
export const CHART_CHROME = {
    grid: '#e1e0d9',
    axis: '#c3c2b7',
    mutedInk: '#898781',
    secondaryInk: '#52514e',
    primaryInk: '#0b0b0b',
    surface: '#ffffff',
};

/**
 * Stable colour assignment.
 *
 * Colour must follow the *entity*, never its rank. Series are ranked by total to
 * decide who gets plotted, but if we also coloured by that rank then changing the
 * date range would reshuffle the ranking and repaint every surviving line — the
 * reader would think the data changed identity. So an allocator remembers which
 * slot each entity was given and hands it back on later runs.
 *
 * Create one allocator per chart dimension and keep it for the life of the page
 * (a useRef, not state — reassigning must not trigger a render).
 *
 *   const alloc = createSlotAllocator();
 *   const colorMap = alloc(orderedKeys);   // { entityKey: '#rrggbb' }
 *
 * `seed` optionally pins entities to specific slots up front, for dimensions
 * whose order is semantic rather than data-driven (the three pass types).
 */
export function createSlotAllocator(seed) {
    // entity key -> slot index. Remembered even while an entity is off-screen, so
    // it gets its original colour back if it re-enters and the slot is still free.
    const memory = { ...(seed || {}) };

    return function allocate(orderedKeys) {
        const shown = (orderedKeys || []).filter(k => k !== OTHER_KEY);
        const slots = {};
        const used = new Set();

        // Pass 1 — honour remembered slots where they're still free.
        shown.forEach(key => {
            const remembered = memory[key];
            if (remembered !== undefined && !used.has(remembered) && remembered < MAX_SERIES) {
                slots[key] = remembered;
                used.add(remembered);
            }
        });

        // Pass 2 — give everyone else the lowest free slot.
        shown.forEach(key => {
            if (slots[key] !== undefined) return;
            let slot = 0;
            while (used.has(slot) && slot < MAX_SERIES) slot++;
            slots[key] = slot % MAX_SERIES;
            used.add(slots[key]);
            memory[key] = slots[key];
        });

        const map = {};
        Object.keys(slots).forEach(key => { map[key] = SERIES_COLORS[slots[key]]; });
        map[OTHER_KEY] = OTHER_COLOR;
        return map;
    };
}
